import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateModal({ isOpen, onClose }: CreateModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden'; // Prevent scroll
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Build content types with workspace slug in paths
  const contentTypes = [
    {
      id: 'blog',
      icon: '📝',
      title: t('dashboard.create.types.blog.title'),
      description: t('dashboard.create.types.blog.description'),
      path: `/${workspaceSlug}/create?type=blog`,
    },
    {
      id: 'idea',
      icon: '💡',
      title: t('dashboard.create.types.idea.title'),
      description: t('dashboard.create.types.idea.description'),
      path: `/${workspaceSlug}/create?type=idea`,
    },
    {
      id: 'video',
      icon: '🎥',
      title: t('dashboard.create.types.video.title'),
      description: t('dashboard.create.types.video.description'),
      path: `/${workspaceSlug}/create?type=video`,
    },
    {
      id: 'brainstorm',
      icon: '🤔',
      title: t('dashboard.create.types.brainstorm.title'),
      description: t('dashboard.create.types.brainstorm.description'),
      path: `/${workspaceSlug}/create?type=brainstorm`,
    },
  ];

  const handleSelect = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 border-3 border-black dark:border-gray-600 rounded-lg shadow-brutal-lg max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 rounded-full border-2 border-black dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
          aria-label="Close modal"
        >
          <svg
            className="w-6 h-6 text-black dark:text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <h2 className="text-3xl font-bold mb-2 dark:text-gray-100 pr-12">
          {t('dashboard.create.modal.title')}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
          {t('dashboard.create.modal.subtitle')}
        </p>

        {/* Content Type Options (2x2 Grid) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {contentTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => handleSelect(type.path)}
              className="group p-6 border-2 border-black dark:border-gray-600 rounded-lg hover:shadow-brutal transition-all text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {/* Icon */}
              <div className="text-4xl mb-3">{type.icon}</div>

              {/* Title */}
              <h3 className="text-xl font-bold mb-2 dark:text-gray-100 group-hover:underline">
                {type.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {type.description}
              </p>
            </button>
          ))}
        </div>

        {/* Footer Hint */}
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-500 text-center">
          {t('dashboard.create.modal.hint')}
        </p>
      </div>
    </div>
  );
}
